import { FC, memo } from 'react';
import { Box, Truncatable } from '@bridebook/ui';
import componentStyles from './similar-venue-card.style';
import { IconReviewFilled } from '@bridebook/ui/src/icons';
import { FeaturedMarker } from '@bridebook/ui/src/components/supplier/supplier-card-v2/properties/featured-marker/featured-marker';
import { Icon } from '@bridebook/aura';
import { HeartOutline } from '@bridebook/aura/src/icons';
import { IElasticSupplier } from '@bridebook/web/lib/supplier/types';
import { getPricingValueString, getSupplierThumb } from '@bridebook/web/lib/supplier/utils';
import { getPricingCategoryByIndicator } from '@bridebook/web/app-shared/lib/supplier/utils';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { Link } from '@bridebook/web/app-shared/components/link/link';
import { getSupplierUrl } from '@bridebook/toolbox/src';
import { useSupplierBucketSetting } from '@bridebook/web/app-basic/lib/hooks/use-supplier-bucket-setting';

interface IProps {
  supplier: IElasticSupplier;
}

const SimilarVenueCard: FC<IProps> = ({ supplier }) => {
  const styles = componentStyles();
  const market = useMarket();
  const { name, town, reviewsCount, reviewsStars, pricingIndicator, supplierBoosted } = supplier;
  const { isUnlocked: isFeatureSupplierMarkerUnlocked } = useSupplierBucketSetting(
    'featuredSupplierMarker',
    supplier.type,
    supplier.tier,
  );

  const currency = market.currency;
  const pricingCategory = getPricingCategoryByIndicator(pricingIndicator);

  const photo = getSupplierThumb(supplier);

  const targetUrl = `/${market.prefix}${getSupplierUrl(supplier)}`;
  const showFeatureSupplierMarker = supplierBoosted || isFeatureSupplierMarkerUnlocked;

  return (
    <Link href={targetUrl} target="_blank">
      <Box style={styles.container}>
        <Box style={styles.imgBox}>
          <Box style={styles.favorite}>
            <Icon icon={HeartOutline} size={22} className="text-blush-tangerine-120" />
          </Box>
          {showFeatureSupplierMarker && (
            <Box data-name="featured-line" style={styles.featuredMarkerWrap}>
              <FeaturedMarker />
            </Box>
          )}
          <Box as="img" style={styles.img} src={photo} />
        </Box>
        <Box as="h5" style={styles.title}>
          <Truncatable text={name} />
        </Box>
        <Box style={styles.review}>
          {town}
          {reviewsCount > 0 && reviewsStars > 0 && (
            <>
              {'  •  '}
              {reviewsStars.toFixed(2)} <IconReviewFilled color="peachRose120" width={12} />
              <Box as="span" style={styles.count}>
                ({reviewsCount})
              </Box>
            </>
          )}
        </Box>
        <Box style={styles.price}>
          {getPricingValueString(currency, market, supplier, pricingCategory, true)}
        </Box>
      </Box>
    </Link>
  );
};

export default memo(SimilarVenueCard);
